// Best-effort reverse geocoding of a coordinate to an ISO 3166-1 alpha-3
// country code, used when saving custom places so the saved country reflects
// where the place actually sits (rather than a hardcoded default).
//
// This talks to Mapbox directly (classic Geocoding v5), not the Pinnacle API,
// so it lives as a small standalone module rather than on a $http mixin. It is
// deliberately best-effort: any failure (bad coordinates, network error,
// unexpected shape, missing token) resolves to null and never throws, so a
// country lookup can never block or fail a save. The server applies the tenant
// country fallback when this returns null.
// Feature: place-country-on-save (Task 2)

import {
  deriveCountryFromReverseGeocode,
  parseReverseGeocodeLabel,
} from "@/components/partials/placeSelection/placeSelectionGeocoderHelpers";

const MAPBOX_GEOCODE_BASE =
  "https://api.mapbox.com/geocoding/v5/mapbox.places";

/**
 * Whether a value is a finite number usable as a coordinate.
 * @param {*} n
 * @returns {boolean}
 */
function isFiniteNumber(n) {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * Reverse geocode a longitude/latitude to an alpha-3 country code.
 *
 * Returns null (never throws) when:
 *  - coordinates are not finite numbers or are out of range,
 *  - the Mapbox access token is not configured,
 *  - the request fails or returns a non-OK status,
 *  - the response has no features or no derivable country.
 *
 * @param {number} lon - Longitude (-180..180).
 * @param {number} lat - Latitude (-90..90).
 * @param {Object} [opts]
 * @param {typeof fetch} [opts.fetchImpl] - Injectable fetch (for tests).
 * @param {string} [opts.accessToken] - Injectable token (defaults to env).
 * @returns {Promise<string|null>} Uppercase alpha-3 code, or null.
 */
export async function reverseGeocodeCountry(lon, lat, opts = {}) {
  const fetchImpl =
    opts.fetchImpl ||
    (typeof fetch === "function" ? fetch : null);
  const accessToken =
    opts.accessToken || process.env.VUE_APP_MAP_ACCESS_TOKEN;

  if (!isFiniteNumber(lon) || !isFiniteNumber(lat)) return null;
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null;
  if (!accessToken || !fetchImpl) return null;

  const url =
    `${MAPBOX_GEOCODE_BASE}/${lon},${lat}.json` +
    `?types=country&limit=1&access_token=${encodeURIComponent(accessToken)}`;

  try {
    const response = await fetchImpl(url);
    if (!response || !response.ok) return null;

    const body = await response.json();
    const feature =
      body && Array.isArray(body.features) ? body.features[0] : null;
    if (!feature) return null;

    return deriveCountryFromReverseGeocode(feature);
  } catch (_err) {
    // Best-effort: swallow all errors and let the caller fall back.
    return null;
  }
}

/**
 * Reverse geocode a longitude/latitude to a human-readable `{ city, country }`
 * label pair, used to autofill a place's address from its location.
 *
 * Like {@link reverseGeocodeCountry} this is deliberately best-effort: it never
 * throws and resolves to `{ city: null, country: null }` when:
 *  - coordinates are not finite numbers or are out of range,
 *  - the Mapbox access token is not configured,
 *  - the request fails or returns a non-OK status,
 *  - the response cannot be parsed into any label.
 *
 * Requests `types=place,locality,country` so a single call yields both a city
 * (place/locality) and a country feature; parsing is delegated to the pure
 * {@link parseReverseGeocodeLabel} helper.
 *
 * @param {number} lon - Longitude (-180..180).
 * @param {number} lat - Latitude (-90..90).
 * @param {Object} [opts]
 * @param {typeof fetch} [opts.fetchImpl] - Injectable fetch (for tests).
 * @param {string} [opts.accessToken] - Injectable token (defaults to env).
 * @returns {Promise<{ city: string|null, country: string|null }>}
 */
export async function reverseGeocodePlaceLabel(lon, lat, opts = {}) {
  const empty = { city: null, country: null };

  const fetchImpl =
    opts.fetchImpl || (typeof fetch === "function" ? fetch : null);
  const accessToken =
    opts.accessToken || process.env.VUE_APP_MAP_ACCESS_TOKEN;

  if (!isFiniteNumber(lon) || !isFiniteNumber(lat)) return empty;
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return empty;
  if (!accessToken || !fetchImpl) return empty;

  // Note: Mapbox Geocoding v5 rejects `limit` when combined with multiple
  // `types` on reverse geocoding ("limit must be combined with a single type
  // parameter when reverse geocoding"). Omit `limit` so the request stays
  // valid; reverse geocoding returns one feature per type in the hierarchy
  // (place, locality, country), which is what parseReverseGeocodeLabel needs.
  const url =
    `${MAPBOX_GEOCODE_BASE}/${lon},${lat}.json` +
    `?types=place,locality,country` +
    `&access_token=${encodeURIComponent(accessToken)}`;

  try {
    const response = await fetchImpl(url);
    if (!response || !response.ok) return empty;

    const body = await response.json();
    return parseReverseGeocodeLabel(body);
  } catch (_err) {
    // Best-effort: swallow all errors and let the caller fall back.
    return empty;
  }
}
